import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Document } from 'react-pdf';
// Sets pdfjs.GlobalWorkerOptions.workerSrc to the bundled worker asset.
import '@/lib/pdf-worker';

type DocumentProps = Parameters<typeof Document>[0];

type KeyedPdfDocumentProps = DocumentProps & {
  /** Identity of the PDF; a change fully remounts the document and its pages. */
  url: string;
  /** Extra remount trigger, e.g. a manual retry counter. */
  retryKey?: number;
};

interface StaleDocumentBoundaryProps {
  fallback: ReactNode;
  children: ReactNode;
}

/**
 * Catches render/effect errors thrown by react-pdf children. pdf.js throws
 * `Cannot read properties of null (reading 'sendWithPromise')` when a `Page`
 * calls `getPage` on a document that was just destroyed; that race is benign,
 * so show the viewer's own error UI instead of crashing the surrounding panel.
 * Not reported to Sentry on purpose.
 */
class StaleDocumentBoundary extends Component<
  StaleDocumentBoundaryProps,
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.warn('PDF document render failed', error, info.componentStack);
  }

  render(): ReactNode {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

/**
 * react-pdf `Document` that remounts (with all its `Page` children) whenever
 * `url` changes, so no `Page` can hold a destroyed `PDFDocumentProxy`. Every
 * PDF viewer must use this instead of importing `Document` directly.
 */
export function KeyedPdfDocument({
  url,
  retryKey = 0,
  error,
  ...documentProps
}: KeyedPdfDocumentProps) {
  const identity = `${url}#${retryKey}`;
  return (
    <StaleDocumentBoundary
      key={identity}
      fallback={typeof error === 'function' ? error() : error}
    >
      <Document file={url} error={error} {...documentProps} />
    </StaleDocumentBoundary>
  );
}
